import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Star, Search, ChevronLeft, ChevronRight, Trophy, MapPin, Briefcase, ShieldCheck, Clock
} from 'lucide-react';
import RatingCriteriaDialog from '../../components/RatingCriteriaDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StarDisplay = ({ rating, size = 14 }) => {
  if (rating === null || rating === undefined) return <span className="text-gray-400 text-xs">Not rated</span>;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star key={s} size={size} className={s <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} />
      ))}
      <span className="text-sm text-gray-600 ml-1">({rating.toFixed(1)})</span>
    </div>
  );
};

const EditableRating = ({ value, onSave, label, color = 'yellow' }) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(0);
  const [pending, setPending] = useState(false);

  const handleClick = async (star) => {
    setPending(true);
    await onSave(star);
    setPending(false);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 cursor-pointer group" onClick={() => setEditing(true)} data-testid={`edit-${label}-rating`}>
        {value ? (
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} size={16} className={s <= value ? `text-${color}-500 fill-${color}-500` : 'text-gray-300'} />
            ))}
            <span className="text-sm font-medium ml-1">{value.toFixed(1)}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm group-hover:text-gray-600">Click to rate</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <Star
          key={s}
          size={20}
          className={`cursor-pointer transition-colors ${
            s <= (hover || value || 0)
              ? color === 'purple' ? 'text-purple-500 fill-purple-500' : 'text-yellow-500 fill-yellow-500'
              : 'text-gray-300 hover:text-gray-400'
          }`}
          onMouseEnter={() => setHover(s)}
          onMouseLeave={() => setHover(0)}
          onClick={() => !pending && handleClick(s)}
        />
      ))}
      <button className="text-xs text-gray-400 ml-1 hover:text-gray-600" onClick={() => setEditing(false)}>cancel</button>
    </div>
  );
};

const AdminRatings = () => {
  const { getAuthHeader, userType } = useAuth();
  const [users, setUsers] = useState([]);
  const [topAudited, setTopAudited] = useState([]);
  const [todayWorkers, setTodayWorkers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogWorker, setDialogWorker] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const openRatingDialog = (worker) => {
    setDialogWorker(worker);
    setDialogOpen(true);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchRatings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ratings`, {
        params: { page, per_page: 25, search: searchDebounced },
        ...getAuthHeader()
      });
      setUsers(res.data.users);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  }, [page, searchDebounced, getAuthHeader]);

  const fetchTopAudited = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ratings/top-audited`, getAuthHeader());
      setTopAudited(res.data);
    } catch (err) {
      // silent
    }
  }, [getAuthHeader]);

  const fetchTodayWorkers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ratings/today`, getAuthHeader());
      setTodayWorkers(res.data.workers || []);
    } catch (err) {
      // silent
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchRatings(); }, [fetchRatings]);
  useEffect(() => { fetchTopAudited(); fetchTodayWorkers(); }, [fetchTopAudited, fetchTodayWorkers]);

  const handleSetRating = async (userId, rating) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/rate`, { rating }, getAuthHeader());
      toast.success('Rating updated');
      fetchRatings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update rating');
    }
  };

  const handleSetAuditRating = async (userId, rating) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/audit-rate`, { rating }, getAuthHeader());
      toast.success('Audit rating updated');
      fetchRatings();
      fetchTopAudited();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update audit rating');
    }
  };

  const handleDailyRated = () => {
    fetchTodayWorkers();
    fetchRatings();
    fetchTopAudited();
  };

  const canEdit = userType === 'admin' || userType === 'auditor';

  return (
    <div className="flex gap-6" data-testid="admin-ratings-page">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Ratings</h1>
            <p className="text-sm text-gray-500 mt-1">
              {total} users total
              {!canEdit && (
                <span
                  className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 align-middle"
                  data-testid="ratings-read-only-badge"
                  title="Managers can view ratings but only Admins/Auditors can submit them"
                >
                  Read-only
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, email, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="ratings-search-input"
          />
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ratings-table">
              <thead>
                <tr className="bg-gray-50 border-b text-left">
                  <th className="px-4 py-3 font-medium text-gray-600">User</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Location</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-center">Jobs</th>
                  <th className="px-4 py-3 font-medium text-gray-600">Rating</th>
                  <th className="px-4 py-3 font-medium text-gray-600">
                    <div className="flex items-center gap-1">
                      <ShieldCheck size={14} className="text-purple-500" /> Audit Rating
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-gray-400">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-gray-400">No users found</td></tr>
                ) : users.map(user => (
                  <tr key={user.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`rating-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                      </div>
                      <div className="flex gap-1 mt-1">
                        {user.is_pro && <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5 py-0">PRO</Badge>}
                        {user.disabled && <Badge className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0">Disabled</Badge>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-gray-600">
                        <MapPin size={13} className="text-gray-400 shrink-0" />
                        <span className="truncate max-w-[180px]">{user.location || 'No location'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1 text-gray-700">
                        <Briefcase size={13} className="text-gray-400" />
                        <span className="font-medium">{user.jobs_completed}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <EditableRating value={user.star_rating} onSave={(r) => handleSetRating(user.id, r)} label="star" color="yellow" />
                      ) : (
                        <StarDisplay rating={user.star_rating} />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {canEdit ? (
                        <EditableRating value={user.audit_rating} onSave={(r) => handleSetAuditRating(user.id, r)} label="audit" color="purple" />
                      ) : (
                        <StarDisplay rating={user.audit_rating} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t" data-testid="ratings-pagination">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft size={16} className="mr-1" /> Prev
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Right sidebar */}
      <div className="w-72 shrink-0 hidden lg:block space-y-4">
        {/* Today's Ratings */}
        <Card className="sticky top-4 ring-2 ring-amber-400 shadow-lg">
          <div className="p-4 border-b bg-amber-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-amber-600" />
                <h3 className="font-semibold text-amber-900">Today's Auditor Ratings</h3>
              </div>
              <Badge className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 animate-pulse">REQUIRED</Badge>
            </div>
            <p className="text-xs text-amber-600 mt-1">
              {todayWorkers.filter(w => !w.today_rated).length} of {todayWorkers.length} need rating
            </p>
          </div>
          <div className="divide-y max-h-[360px] overflow-y-auto" data-testid="today-ratings-list">
            {todayWorkers.length === 0 ? (
              <p className="p-4 text-sm text-gray-400 text-center">No workers active today</p>
            ) : todayWorkers.map(worker => (
              <div key={worker.id} className={`px-4 py-3 ${!worker.today_rated ? 'bg-amber-50/40' : ''}`} data-testid={`today-worker-${worker.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <button
                    type="button"
                    onClick={() => openRatingDialog(worker)}
                    className="font-medium text-sm text-gray-900 truncate hover:text-amber-700 hover:underline text-left"
                    data-testid={`today-worker-name-${worker.id}`}
                    title={worker.today_rated ? 'View rating breakdown' : `Rate ${worker.name}`}
                  >
                    {worker.name}
                  </button>
                  {worker.today_rated && (
                    <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 shrink-0 ml-1">Done</Badge>
                  )}
                </div>
                {worker.today_rated ? (
                  <button
                    type="button"
                    onClick={() => openRatingDialog(worker)}
                    className="flex items-center gap-0.5"
                    title="View rating breakdown"
                    data-testid={`today-worker-rated-${worker.id}`}
                  >
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={13} className={s <= (worker.today_rating || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
                    ))}
                    <span className="text-xs text-gray-500 ml-1">{worker.today_rating?.toFixed(1)}</span>
                  </button>
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={() => openRatingDialog(worker)}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
                    data-testid={`today-worker-rate-btn-${worker.id}`}
                  >
                    Rate
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">Pending</span>
                )}
                {worker.audit_rating && (
                  <p className="text-[10px] text-gray-400 mt-1">Avg audit: {worker.audit_rating.toFixed(1)}</p>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* Top Auditor Rated */}
        <Card>
          <div className="p-4 border-b bg-purple-50">
            <div className="flex items-center gap-2">
              <Trophy size={18} className="text-purple-600" />
              <h3 className="font-semibold text-purple-900">Top Auditor Rated</h3>
            </div>
            <p className="text-xs text-purple-600 mt-1">Top 10 by audit rating</p>
          </div>
          <div className="divide-y" data-testid="top-audited-list">
            {topAudited.length === 0 ? (
              <p className="p-4 text-sm text-gray-400 text-center">No audit ratings yet</p>
            ) : topAudited.map((user, idx) => (
              <div key={user.id} className="px-4 py-3 flex items-start gap-3" data-testid={`top-audited-${idx}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                  idx === 1 ? 'bg-gray-100 text-gray-600' :
                  idx === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-gray-50 text-gray-500'
                }`}>
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{user.name}</p>
                  <div className="flex items-center gap-0.5 mt-0.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} size={11} className={s <= (user.audit_rating || 0) ? 'text-purple-500 fill-purple-500' : 'text-gray-300'} />
                    ))}
                    <span className="text-xs text-gray-500 ml-1">{user.audit_rating?.toFixed(1)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                    <span>{user.jobs_completed} jobs</span>
                    {user.location && <span className="truncate max-w-[100px]">{user.location}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <RatingCriteriaDialog
        worker={dialogWorker}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        canEdit={canEdit}
        onRated={handleDailyRated}
      />
    </div>
  );
};

export default AdminRatings;
